import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { ShieldCheck, Settings as SettingsIcon } from 'lucide-react';
import {
  AppLayout,
  AppSwitcher,
  toWorkspaceApps,
  PlatformTopbar,
  SharedSidebarShell,
  resolveWorkspaceNav,
  TenantSwitcher,
  TrialBanner,
  UserMenu,
  UserMenuItem,
  accountSubtitle,
} from '@zafroi/ui-kit';
import { MAVK_TRIAL } from '@/config/trial';
import type { SidebarNavGroup, UserMenuIdentity } from '@zafroi/ui-kit';
import { sidebarNav } from '@/config/nav';
import { Logo } from '@/components/Logo';
import { useTenant } from '@/services/tenant-context';
import { useAuth } from '@/services/auth-context';
// Engage Assistant (⌘J staff copilot) hidden from MVP shell 2026-05-24 —
// Engage is a customer-engagement product, internal staff tooling is out of
// scope for now. Code preserved at `src/modules/assistant/` and the
// `engage.assistant` runtime module is unchanged — re-importing these three
// lines and the topbar/panel/provider usages below revives it as-is.
// import { AssistantProvider } from '@/modules/assistant/assistant-context';
// import { AssistantTrigger } from '@/modules/assistant/AssistantTrigger';
// import { AssistantPanel } from '@/modules/assistant/AssistantPanel';
import { useEngageInboxCounts } from '@/hooks/useEngageInboxCounts';

const COLLAPSE_KEY = 'zafroi.engage.sidebar.collapsed.v1';
const BASE_DOC_TITLE = 'ZAFROI Engage';
// Plane boundary (ADR-024): platform admins belong on the control plane; a
// product surface redirects them there. Fresh vs cache — auth-context sets
// status='authenticated' only after /auth/me, so user.tier isn't stale.
const PLATFORM_APP_URL =
  import.meta.env.VITE_PLATFORM_APP_URL || 'https://platform.zafroi.com/dashboard';

export function AppShell() {
  const navigate = useNavigate();
  const { tenants, currentTenant, setCurrentTenant } = useTenant();
  const { user, logout, status } = useAuth();
  const isPlatformAdmin = status === 'authenticated' && user?.tier === 'PLATFORM_ADMIN';
  useEffect(() => {
    if (isPlatformAdmin) window.location.replace(PLATFORM_APP_URL);
  }, [isPlatformAdmin]);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === 'true';
  });

  // "Work-to-do" signal — drives sidebar badges, the topbar bell, and the
  // document title. Reads from React Query caches the rest of the app
  // already populates, so it's free.
  const counts = useEngageInboxCounts();

  // Reflect total work-to-do in the document title so the agent sees the
  // signal even when the engage-app tab is in the background.
  useEffect(() => {
    document.title =
      counts.total > 0 ? `(${counts.total}) ${BASE_DOC_TITLE}` : BASE_DOC_TITLE;
    return () => {
      document.title = BASE_DOC_TITLE;
    };
  }, [counts.total]);

  // Decorate the static nav config with reactive counts at render time. We
  // keep `nav.ts` static (per-tenant counts can't live in a module-level
  // config) and wrap the relevant entries with their current badge here.
  // ADR-041/051: on the unified workspace nav (COMMERCE once activated, SOFTWARE
  // today) the runtime serves ONE cross-app sidebar, already permission-filtered.
  // Engage renders it — resolved against `workspace.apps` for cross-app hrefs —
  // so a tenant navigates the whole workspace from here. Absent → the local Engage
  // nav (with its live counts) exactly as before.
  const unifiedNav = user?.workspace?.nav ?? null;
  const decoratedNav = useMemo(
    () =>
      unifiedNav && unifiedNav.length
        ? resolveWorkspaceNav(unifiedNav, 'engage', user?.workspace?.apps)
        : decorateNavWithCounts(sidebarNav, counts),
    [counts, unifiedNav, user?.workspace?.apps],
  );

  // Live user identity from the JWT session. Falls back to "—" if for some
  // reason the user is missing (AuthGuard should have already redirected,
  // so this is defensive only).
  const userMenuIdentity: UserMenuIdentity = useMemo(
    () =>
      user
        ? {
            name: user.fullName,
            email: user.email,
            subtitle: accountSubtitle({ tier: user.tier, role: user.role, roles: user.roles }),
          }
        : { name: '—', subtitle: 'Not signed in' },
    [user],
  );

  const handleToggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // non-fatal
      }
      return next;
    });
  };

  const handleSignOut = async () => {
    await logout();
    // The AuthGuard will see the unauthenticated state on the next render
    // and hard-redirect to the canonical zafroi.com/login. Sending the user
    // to local /login here is fine — it'll bounce them through the same
    // path — but we avoid the double-redirect by just blanking the
    // location now.
    navigate('/dashboard', { replace: true });
  };

  // Platform admins are redirected to the control plane (effect above).
  if (isPlatformAdmin) {
    return (
      <div className="flex h-screen w-screen items-center justify-center text-sm text-muted-foreground">
        Redirecting to the control plane…
      </div>
    );
  }

  return (
    <AppLayout
      banner={<TrialBanner config={MAVK_TRIAL} />}
      sidebar={
        <SharedSidebarShell
          nav={decoratedNav}
          collapsed={collapsed}
          onToggle={handleToggle}
          header={
            <Logo
              variant={collapsed ? 'mark' : 'lockup'}
              tagline="Engage"
              glow
            />
          }
          footer={
            !collapsed ? (
              <div className="flex items-center justify-between">
                <span>v0.1.0</span>
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] uppercase tracking-wider">
                  MVP1
                </span>
              </div>
            ) : null
          }
        />
      }
      topbar={
        <PlatformTopbar
          leading={
            <>
              {/* Single-workspace tenants (SOFTWARE's unified nav, REAL_ESTATE's single
                  app) never see the ecosystem launcher — the server decides, once, in
                  `/auth/me` → `workspace.singleWorkspace` (ADR-038/041). Engage rendered
                  it unconditionally, so those verticals saw a switcher here even though
                  every other surface hid it. */}
              {!user?.workspace?.singleWorkspace && (
                <>
                  <AppSwitcher
                    apps={toWorkspaceApps(user?.workspace?.apps)}
                    currentAppId="engage"
                    onAppSelect={(app) => {
                      if (app.id === 'engage') {
                        navigate('/dashboard');
                        return false;
                      }
                      return undefined;
                    }}
                  />
                  <span className="hidden text-sm text-muted-foreground md:inline">
                    /
                  </span>
                </>
              )}
              <TenantSwitcher
                tenants={tenants}
                currentTenantId={currentTenant.id}
                onTenantChange={setCurrentTenant}
              />
            </>
          }
          // Global command-palette / search will land here. Removed the
          // placeholder button (no onClick, misleading copy mentioning
          // removed/demoted modules). See BACKLOG.md "Global search".
          center={null}
          trailing={
            <UserMenu user={userMenuIdentity} onSignOut={handleSignOut}>
              <UserMenuItem onSelect={() => navigate('/settings')}>
                <SettingsIcon className="h-4 w-4" />
                Account settings
              </UserMenuItem>
              <UserMenuItem>
                <ShieldCheck className="h-4 w-4" />
                Security
              </UserMenuItem>
            </UserMenu>
          }
        />
      }
    >
      <Outlet />
    </AppLayout>
  );
}

function countForItem(
  id: string,
  counts: ReturnType<typeof useEngageInboxCounts>,
): number {
  if (id === 'support') return counts.pendingSupport;
  if (id === 'chatbot') return counts.draftKnowledge;
  return 0;
}

function CountBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-sidebar-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-sidebar-accent">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function decorateNavWithCounts(
  nav: SidebarNavGroup[],
  counts: ReturnType<typeof useEngageInboxCounts>,
): SidebarNavGroup[] {
  return nav.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      const count = countForItem(item.id, counts);
      if (count <= 0) return item;
      return {
        ...item,
        badge: <CountBadge count={count} />,
      };
    }),
  }));
}
